import { Injectable } from '@angular/core';
import { CustomResponse } from '@app/core';
import { ReportCommentCategory } from '@app/core/reports';
import { RequestService } from '@app/core/request';
import { TableLazyLoadEvent } from 'primeng/table';
import { firstValueFrom } from 'rxjs';

export type ReportComment = {
  comment: string;
  category?: ReportCommentCategory;
  reportId: number;
  createdAt?: string | Date;
  updatedAt?: string;
  id?: number;
  username?: string;
  file?: string; // Optional file name associated with the comment
};

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  constructor(private request: RequestService) {}

  async createComment(
    reportId: number,
    comment: string,
    category?: string,
    file?: string // Optional file name to associate with the comment
  ): Promise<any> {
    // Here you would typically send the comment to the server
    console.log(`Comment created for report ${reportId}:`, {
      comment,
      category,
      file: file || 'No file attached',
    });

    await firstValueFrom(
      this.request.post<{
        comment: string;
        category: string;
        reportId: number;
        file?: string; // Optional file name to associate with the comment
      }>(`/api/comments`, {
        reportId,
        comment,
        category,
        file, // Include the file name if provided
      })
    );
  }

  async deleteComment(commentId: number): Promise<void> {
    await firstValueFrom(this.request.delete(`/api/comments/${commentId}`));
  }

  updateComment(
    commentId: number,
    updatedComment: string,
    category?: string
  ): void {
    // Here you would typically send the updated comment to the server
    console.log(`Comment with ID ${commentId} updated:`, {
      updatedComment,
      category,
    });

    // Simulate a server response
    setTimeout(() => {
      console.log('Comment successfully updated.');
    }, 1000);
  }

  async getCommentsByReport(
    reportId: number
  ): Promise<CustomResponse<ReportComment[]>> {
    // Here you would typically fetch comments from the server
    console.log(`Fetching comments for report ${reportId}`);

    const response = await firstValueFrom(
      this.request.get<CustomResponse<ReportComment[]>>(
        `/api/comments/?reportId=${reportId}`
      )
    );
    return response;
  }

  async getComments(
    pagination: TableLazyLoadEvent = {}
  ): Promise<CustomResponse<ReportComment[]>> {
    let params = {};
    if (pagination.first !== undefined) {
      params['offset'] = pagination.first;
    }

    if (pagination.rows !== undefined) {
      params['limit'] = pagination.rows;
    }

    if (pagination.sortField && pagination.sortOrder) {
      params['orderBy'] = pagination.sortField;
      params['orderDirection'] = pagination.sortOrder === 1 ? 'asc' : 'desc';
    }
    const filters = pagination.filters as Record<string, any>;
    let filter = '';
    if (filters && Object.keys(filters).length > 0) {
      const filterParams = Object.entries(filters)
        .filter(([key, value]) => value && value.length > 0 && value[0]?.value)
        .map(([key, value]) => [key, value[0]?.value])
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
      console.log({ filterParams });
      params = {
        ...params,
        ...filterParams,
      };
    }

    const response = await firstValueFrom(
      this.request.get<CustomResponse<ReportComment[]>>(`/api/comments`, params)
    );
    return response;
  }

  getCommentById(commentId: number): void {
    // Here you would typically fetch a specific comment from the server
    console.log(`Fetching comment with ID ${commentId}`);

    // Simulate a server response
    setTimeout(() => {
      console.log('Comment successfully fetched.');
    }, 1000);
  }

  getCommentsByCategory(reportId: number, category: string): void {
    // Here you would typically fetch comments by category from the server
    console.log(
      `Fetching comments for report ${reportId} in category ${category}`
    );

    // Simulate a server response
    setTimeout(() => {
      console.log('Comments successfully fetched by category.');
    }, 1000);
  }

  getCommentsByUser(userId: number): void {
    // Here you would typically fetch comments made by a specific user
    console.log(`Fetching comments made by user ${userId}`);

    // Simulate a server response
    setTimeout(() => {
      console.log('Comments successfully fetched by user.');
    }, 1000);
  }

  getCommentsByReportAndUser(reportId: number, userId: number): void {
    // Here you would typically fetch comments for a specific report made by a specific user
    console.log(
      `Fetching comments for report ${reportId} made by user ${userId}`
    );

    // Simulate a server response
    setTimeout(() => {
      console.log('Comments successfully fetched for report and user.');
    }, 1000);
  }

  getCommentsByDateRange(
    reportId: number,
    startDate: Date,
    endDate: Date
  ): void {
    // Here you would typically fetch comments within a date range for a specific report
    console.log(
      `Fetching comments for report ${reportId} from ${startDate} to ${endDate}`
    );

    // Simulate a server response
    setTimeout(() => {
      console.log('Comments successfully fetched by date range.');
    }, 1000);
  }
}
